/**
 * Playbook Detail Page
 * View and edit a single playbook
 */

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Edit,
  Save,
  X,
  Tag,
  Clock,
  BookOpen,
  Globe,
  Lock,
  Download,
} from "lucide-react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import apiService from "../services/api";

interface Playbook {
  id: number;
  title: string;
  description: string;
  markdown_content: string;
  category: string;
  tags: string[];
  is_public: boolean;
  is_system: boolean;
  version: number;
  created_at: string;
  updated_at: string;
  created_by: number;
}

export const PlaybookDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // State
  const [playbook, setPlaybook] = useState<Playbook | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Edit state
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editContent, setEditContent] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editTags, setEditTags] = useState("");

  // Categories
  const categories = [
    "code_generation",
    "document_processing",
    "data_transformation",
    "api_integration",
    "testing",
    "deployment",
    "other",
  ];

  // Fetch playbook
  const fetchPlaybook = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("auth_token");
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

      const response = await axios.get(`${apiUrl}/api/playbooks/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setPlaybook(response.data);
      setError(null);
    } catch (err: any) {
      console.error("Failed to fetch playbook:", err);
      setError(err.response?.data?.detail || "Failed to load playbook");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchPlaybook();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleEdit = () => {
    if (playbook) {
      setEditTitle(playbook.title);
      setEditDescription(playbook.description);
      setEditContent(playbook.markdown_content);
      setEditCategory(playbook.category);
      setEditTags(playbook.tags.join(", "));
      setIsEditing(true);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem("auth_token");
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

      await axios.put(
        `${apiUrl}/api/playbooks/${id}`,
        {
          title: editTitle,
          description: editDescription,
          markdown_content: editContent,
          category: editCategory,
          tags: editTags
            .split(",")
            .map((t) => t.trim())
            .filter((t) => t),
          change_summary: "Updated via web UI",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // Refresh playbook
      await fetchPlaybook();
      setIsEditing(false);
    } catch (err: any) {
      console.error("Failed to update playbook:", err);
      alert(err.response?.data?.detail || "Failed to update playbook");
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!playbook || !id) return;

    try {
      if (playbook.is_public) {
        await apiService.unpublishPlaybook(parseInt(id));
        alert("Playbook unpublished successfully");
      } else {
        await apiService.publishPlaybook(parseInt(id));
        alert("Playbook published successfully");
      }
      // Refresh playbook
      await fetchPlaybook();
    } catch (err: any) {
      console.error("Failed to publish/unpublish playbook:", err);
      alert(
        err.response?.data?.detail || "Failed to publish/unpublish playbook",
      );
    }
  };

  const handleExport = async () => {
    if (!id) return;

    try {
      await apiService.exportPlaybook(parseInt(id));
    } catch (err: any) {
      console.error("Failed to export playbook:", err);
      alert(err.response?.data?.detail || "Failed to export playbook");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-300 dark:border-gray-600 border-t-blue-600"></div>
          <p className="mt-4 text-gray-500 dark:text-gray-400">
            Loading playbook...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400">{error}</p>
          <button
            onClick={() => navigate("/playbooks")}
            className="mt-4 text-blue-600 hover:text-blue-700 dark:text-blue-400"
          >
            Back to Playbooks
          </button>
        </div>
      </div>
    );
  }

  if (!playbook) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/playbooks")}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="flex items-center gap-3">
                <BookOpen className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                <div>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      className="text-2xl font-bold text-gray-900 dark:text-white bg-transparent border-b-2 border-blue-500 focus:outline-none"
                    />
                  ) : (
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {playbook.title}
                    </h1>
                  )}
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Version {playbook.version}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {!isEditing && (
                <>
                  <button
                    onClick={handleExport}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    title="Export playbook"
                  >
                    <Download className="h-5 w-5" />
                    Export
                  </button>
                  {!playbook.is_system && (
                    <button
                      onClick={handlePublish}
                      className={`inline-flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                        playbook.is_public
                          ? "border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                          : "border-green-600 bg-green-600 text-white hover:bg-green-700"
                      }`}
                      title={
                        playbook.is_public ? "Make private" : "Make public"
                      }
                    >
                      {playbook.is_public ? (
                        <>
                          <Lock className="h-5 w-5" />
                          Unpublish
                        </>
                      ) : (
                        <>
                          <Globe className="h-5 w-5" />
                          Publish
                        </>
                      )}
                    </button>
                  )}
                </>
              )}
              {isEditing ? (
                <>
                  <button
                    onClick={handleCancel}
                    disabled={saving}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                  >
                    <X className="h-5 w-5" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
                  >
                    <Save className="h-5 w-5" />
                    {saving ? "Saving..." : "Save"}
                  </button>
                </>
              ) : (
                <button
                  onClick={handleEdit}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  <Edit className="h-5 w-5" />
                  Edit
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {isEditing ? "Description" : "About"}
              </h2>
              {isEditing ? (
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              ) : (
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  {playbook.description}
                </p>
              )}

              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 mt-6">
                Content
              </h2>
              {isEditing ? (
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  rows={20}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              ) : (
                <div className="prose dark:prose-invert max-w-none">
                  <ReactMarkdown>{playbook.markdown_content}</ReactMarkdown>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Metadata */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Details
              </h3>

              <div className="space-y-4">
                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Category
                  </label>
                  {isEditing ? (
                    <select
                      value={editCategory}
                      onChange={(e) => setEditCategory(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {categories.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat
                            .replace("_", " ")
                            .replace(/\b\w/g, (l) => l.toUpperCase())}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Tag className="h-4 w-4 text-gray-400" />
                      <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded text-sm">
                        {playbook.category.replace("_", " ")}
                      </span>
                    </div>
                  )}
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tags
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editTags}
                      onChange={(e) => setEditTags(e.target.value)}
                      placeholder="tag1, tag2, tag3"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {playbook.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Timestamps */}
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700 space-y-2 text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    <span>
                      Created:{" "}
                      {new Date(playbook.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    <span>
                      Updated:{" "}
                      {new Date(playbook.updated_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
